import type { AggregatedOrderView } from '@/types/api/aggregated/order'

import { listAllocationRecordValues } from '@/components/trade-desk/utils/list-allocation-record-values'
import { OrderMetadataFields } from '@/types/enums/order'

/** An order whose allocations disagree resolves to `'mixed'`. */
export type DiscretionaryAuthority = boolean | 'mixed'

function fromMetadata(order: AggregatedOrderView): boolean | undefined {
  const value = order.metadata?.[OrderMetadataFields.HasDiscretionaryAuthority]
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return undefined
}

/**
 * Discretion on each allocation record of the order, in
 * `listAllocationRecordValues` record order. A record without its own
 * account configuration value takes the order-level value.
 */
export function resolveAllocationDiscretionaryAuthority(
  order: AggregatedOrderView,
): Array<boolean | undefined> {
  const orderDiscretion =
    order.account_configuration?.is_discretionary ??
    // Orders placed before their producer wrote account_configuration carry
    // discretion only in metadata, with nothing to backfill them from.
    fromMetadata(order)
  return listAllocationRecordValues(
    order,
    (config) => config.is_discretionary,
  ).map((discretion) => discretion ?? orderDiscretion)
}

export function resolveOrderDiscretionaryAuthority(
  order: AggregatedOrderView,
): DiscretionaryAuthority | undefined {
  const distinct = new Set<boolean>()
  for (const discretion of resolveAllocationDiscretionaryAuthority(order)) {
    if (discretion !== undefined) {
      distinct.add(discretion)
    }
  }
  if (distinct.size > 1) {
    return 'mixed'
  }
  return distinct.values().next().value
}

export function formatDiscretionaryAuthority(
  discretion: DiscretionaryAuthority | undefined,
): string | undefined {
  switch (discretion) {
    case undefined:
      return undefined
    case 'mixed':
      return 'Mixed'
    case true:
      return 'Yes'
    case false:
      return 'No'
    default:
      return discretion satisfies never
  }
}
